import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Ticket, ArrowLeft, CheckCircle2, Rocket, Users, Clock, DollarSign } from 'lucide-react';
import { supabase } from '../lib/supabase';
import TiccAIlogo from '../assets/TiccAIlogo.png';

const BetaWaitlist = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const features = [
    {
      icon: <Rocket className="h-5 w-5" />,
      title: "Early Access",
      description: "Be among the first to experience our revolutionary AI-powered support system"
    },
    {
      icon: <Users className="h-5 w-5" />,
      title: "Priority Onboarding",
      description: "Get personalized onboarding support from our engineering team"
    },
    {
      icon: <DollarSign className="h-5 w-5" />,
      title: "Beta Pricing",
      description: "Lock in special pricing exclusively for our beta users"
    },
    {
      icon: <Clock className="h-5 w-5" />,
      title: "Launch Updates",
      description: "Receive regular updates about our progress and launch timeline"
    }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const { error: dbError } = await supabase
        .from('waitlist')
        .insert([{ email }]);

      if (dbError) throw dbError;
      
      setIsSubmitted(true);
    } catch (err: any) {
      if (err.code === '23505') {
        setError('This email is already on the waitlist!');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a111e] relative overflow-hidden flex items-center justify-center py-16">
      {/* Back Button */}
      <button
        onClick={() => navigate('/')}
        className="fixed top-8 left-8 z-20 flex items-center gap-2 px-4 py-2 bg-[#0F1824] rounded-lg border border-white/[0.05] text-white hover:border-pink-500/50 transition-all duration-300 group"
      >
        <ArrowLeft className="h-5 w-5 text-pink-500 group-hover:transform group-hover:-translate-x-1 transition-transform" />
        <span>Back</span>
      </button>

      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 -left-1/4 w-[600px] h-[600px] bg-pink-500/20 rounded-full blur-[180px] animate-pulse" />
        <div className="absolute -bottom-1/4 -right-1/4 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-[180px] animate-pulse-slow" />
      </div>

      <div className="relative z-10 max-w-4xl w-full mx-4">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-8">
            <img src={TiccAIlogo} alt="TiccAI" className="h-12 w-12" />
            <h1 className="text-4xl font-bold text-white">Join the Beta Waitlist</h1>
          </div>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Ticc AI is revolutionizing how engineering teams handle support. Be part of our exclusive beta program.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-[#0F1824] rounded-2xl p-8 border border-white/[0.05] shadow-xl">
            {!isSubmitted ? (
              <>
                <div className="bg-pink-500/5 border border-pink-500/10 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-pink-500/10 rounded-full flex items-center justify-center">
                      <Rocket className="h-4 w-4 text-pink-500" />
                    </div>
                    <h2 className="text-lg font-semibold text-white">Private Beta Access</h2>
                  </div>
                  <p className="text-gray-400">
                    We're currently working with a select group of engineering teams to refine our platform. Join our waitlist to be notified when we're ready to onboard new users.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                      Work Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.1] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      required
                      disabled={isLoading}
                    />
                  </div>

                  {error && (
                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500 text-sm">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-pink-500 text-white py-3 rounded-lg hover:bg-pink-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Joining waitlist...
                      </span>
                    ) : (
                      'Join the Waitlist'
                    )}
                  </button>
                </form>

                <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-400">
                  <Users className="h-4 w-4" />
                  <span>Join 2 beta users</span>
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="h-8 w-8 text-green-500" />
                </div>
                <h2 className="text-xl font-semibold text-white mb-2">You're on the list!</h2>
                <p className="text-gray-400 mb-6">
                  Thanks for your interest! We'll notify you as soon as we're ready to onboard new users.
                </p>
                <button
                  onClick={() => navigate('/')}
                  className="inline-flex items-center gap-2 text-pink-500 hover:text-pink-400 transition-colors"
                >
                  <ArrowLeft className="h-5 w-5" />
                  Back to Home
                </button>
              </div>
            )}
          </div>

          <div className="space-y-4">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-[#0F1824] rounded-xl p-6 border border-white/[0.05] hover:border-pink-500/50 transition-all duration-300 animate-float"
                style={{
                  animation: `float 6s ease-in-out infinite`,
                  animationDelay: `${index * 1.5}s`
                }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-pink-500/10 rounded-full flex items-center justify-center text-pink-500">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="text-white font-medium">{feature.title}</h3>
                    <p className="text-gray-400 text-sm mt-1">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}

            <div 
              className="bg-[#0F1824] rounded-xl p-6 border border-white/[0.05] animate-float"
              style={{
                animation: `float 6s ease-in-out infinite`,
                animationDelay: '6s'
              }}
            >
              <h3 className="text-white font-medium mb-4">What our beta users are saying</h3>
              <blockquote className="text-gray-400 text-sm italic">
                "Having spent over a year on Microsoft's NDS On-call team, I can confidently say that Ticc AI will be a game changer. It's like having an experienced engineer by your side, helping you navigate through complex incidents and reducing MTTR significantly."
              </blockquote>
              <div className="mt-4 flex items-center gap-3">
                <img
                  src="tejas.jpg"
                  alt="Tejas Priyadarshi"
                  className="w-8 h-8 rounded-full object-cover"
                />
                <div className="text-sm">
                  <div className="text-white font-medium">Tejas Priyadarshi</div>
                  <div className="text-gray-400">L3, Microsoft</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
          100% {
            transform: translateY(0px);
          }
        }
      `}</style>
    </div>
  );
};

export default BetaWaitlist;